import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { withTranslation } from 'react-i18next';
import Chart from 'react-apexcharts';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import {
  Alert, Badge, Button, Card, CardBody, CardHeader, Col, Form, Input, Label, Row, Spinner,
} from 'reactstrap';
import {
  Table, Tbody, Td, Th, Thead, Tr,
} from 'react-super-responsive-table';
import 'react-super-responsive-table/dist/SuperResponsiveTableStyle.css';
import BasePagination from '../../components/Common/BasePagination';
import { get } from '../../helpers/api_helper';
import { Can } from '../../utilities/Can';

const EMPTY_SUMMARY = {
  bucket: 'month',
  trend: [],
  reasons: [],
  stock_items: [],
  alerts: { alerts: [], window_days: 14, minimum_count: 2 },
};

const EMPTY_EVENTS = {
  page: 1, per: 50, total: 0, results: [],
};

const SOURCE_LABELS = {
  best_buy_rgm: 'Best Buy',
  happy_returns_rma_item: 'Happy Returns',
  partner_api_return_item: 'Partner API',
  sale_refund_item: 'Sales refund',
};

const REASON_CATEGORY_LABELS = {
  damaged: 'Damaged',
  defective: 'Defective',
  good_unused: 'Good / unused',
  missing_parts: 'Missing parts',
  other: 'Other',
  wrong_part: 'Wrong part',
};

const emptyFilters = () => ({
  from: '',
  to: '',
  stock_id: '',
  order_number: '',
  reason: '',
  reason_category: '',
  source: '',
  listing_id: '',
  customer_email: '',
  bucket: 'month',
  window_days: '14',
  minimum_count: '2',
});

function Returns(props) {
  const { t } = props;
  const [draftFilters, setDraftFilters] = useState(emptyFilters());
  const [filters, setFilters] = useState(emptyFilters());
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [events, setEvents] = useState(EMPTY_EVENTS);
  const [page, setPage] = useState(1);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [eventsLoading, setEventsLoading] = useState(true);
  const [error, setError] = useState('');

  const queryString = useMemo(() => {
    const query = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        query.set(key, key === 'to' ? `${value} 23:59:59` : value);
      }
    });
    return query.toString();
  }, [filters]);

  useEffect(() => {
    let active = true;
    setSummaryLoading(true);
    setError('');
    get(`/api/sales/return-trends/summary?${queryString}`)
      .then((response) => {
        if (active) setSummary({ ...EMPTY_SUMMARY, ...response });
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message);
          toastr.error(t('Error loading return trends'));
        }
      })
      .finally(() => { if (active) setSummaryLoading(false); });
    return () => { active = false; };
  }, [queryString, t]);

  useEffect(() => {
    let active = true;
    const separator = queryString ? '&' : '';
    setEventsLoading(true);
    get(`/api/sales/return-trends?${queryString}${separator}page=${page}&per=50`)
      .then((response) => {
        if (active) setEvents({ ...EMPTY_EVENTS, ...response });
      })
      .catch((requestError) => {
        if (active) {
          setError(requestError.message);
          toastr.error(t('Error loading return events'));
        }
      })
      .finally(() => { if (active) setEventsLoading(false); });
    return () => { active = false; };
  }, [queryString, page, t]);

  const totals = useMemo(() => summary.reasons.reduce((result, row) => ({
    quantity: result.quantity + Number(row.returned_qty || 0),
    events: result.events + Number(row.return_count || 0),
  }), { quantity: 0, events: 0 }), [summary.reasons]);

  const trendOptions = useMemo(() => ({
    chart: { toolbar: { show: false }, zoom: { enabled: false } },
    colors: ['#f1b44c'],
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    xaxis: { categories: summary.trend.map((row) => row.date), labels: { rotate: -45 } },
    yaxis: { min: 0, forceNiceScale: true, title: { text: t('Returned quantity') } },
    tooltip: { shared: true },
    noData: { text: t('No return history for these filters') },
  }), [summary.trend, t]);

  const reasonOptions = useMemo(() => ({
    labels: summary.reasons.map((row) => row.reason || t('Unknown')),
    legend: { position: 'bottom' },
    dataLabels: { enabled: summary.reasons.length <= 8 },
    noData: { text: t('No return reasons for these filters') },
  }), [summary.reasons, t]);

  const updateDraft = (event) => {
    const { name, value } = event.target;
    setDraftFilters((current) => ({ ...current, [name]: value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setPage(1);
    setFilters({ ...draftFilters });
  };

  const resetFilters = () => {
    const cleared = emptyFilters();
    setDraftFilters(cleared);
    setFilters(cleared);
    setPage(1);
  };

  const applyStockFilter = (stockId) => {
    setDraftFilters((current) => ({ ...current, stock_id: stockId }));
    setFilters((current) => ({ ...current, stock_id: stockId }));
    setPage(1);
  };

  const applyReasonFilter = (reason) => {
    setDraftFilters((current) => ({ ...current, reason }));
    setFilters((current) => ({ ...current, reason }));
    setPage(1);
  };

  const renderStockFilterLink = (stockId) => (
    <Button
      type="button"
      color="link"
      className="returns-trends-page__stock-link p-0 border-0"
      onClick={() => applyStockFilter(stockId)}
    >
      {stockId}
    </Button>
  );

  const renderAlertStockLinks = (alert) => {
    if (alert.stock_ids?.length > 0) {
      return alert.stock_ids.map((stockId, index) => (
        <React.Fragment key={stockId}>
          {index > 0 && ', '}
          {renderStockFilterLink(stockId)}
        </React.Fragment>
      ));
    }

    if (alert.stock_id) return renderStockFilterLink(alert.stock_id);
    return t('Unknown');
  };

  const renderReasonFilterLink = (reason) => (
    <Button
      type="button"
      color="link"
      className="returns-trends-page__filter-link p-0 border-0"
      onClick={() => applyReasonFilter(reason)}
    >
      {reason}
    </Button>
  );

  const lastPage = Math.max(1, Math.ceil(Number(events.total || 0) / Number(events.per || 50)));
  const alerts = Array.isArray(summary.alerts?.alerts) ? summary.alerts.alerts : [];

  const alertColor = (alert) => {
    if (alert.severity === 'high') return 'danger';
    if (alert.direction === 'down') return 'success';
    return 'warning';
  };

  const alertLabel = (alert) => {
    if (alert.type === 'same_customer_cluster') return t('Repeat customer');
    if (alert.type === 'wrong_part') return t('Wrong part');
    if (alert.direction === 'down') return t('Trending down');
    return t('Trending up');
  };

  return (
    <div className="page-content returns-trends-page">
      <div className="container-fluid">
        <div className="mb-3">
          <div>
            <h4 className="mb-1">{t('Returns Trends')}</h4>
            <p className="returns-trends-page__subtitle mb-0">
              {t('Return volume, reasons, stock items, and source events')}
            </p>
          </div>
        </div>

        <Card>
          <CardBody>
            <Form onSubmit={applyFilters}>
              <Row className="g-3 align-items-end">
                <Col md="2">
                  <Label for="returns-from">{t('From')}</Label>
                  <Input id="returns-from" type="date" name="from" value={draftFilters.from} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-to">{t('To')}</Label>
                  <Input id="returns-to" type="date" name="to" value={draftFilters.to} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-stock">{t('Stock ID')}</Label>
                  <Input id="returns-stock" name="stock_id" value={draftFilters.stock_id} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-order">{t('Order number')}</Label>
                  <Input id="returns-order" name="order_number" value={draftFilters.order_number} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-source">{t('Source')}</Label>
                  <Input id="returns-source" type="select" name="source" value={draftFilters.source} onChange={updateDraft}>
                    <option value="">{t('All sources')}</option>
                    {Object.entries(SOURCE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{t(label)}</option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <Label for="returns-bucket">{t('Group by')}</Label>
                  <Input id="returns-bucket" type="select" name="bucket" value={draftFilters.bucket} onChange={updateDraft}>
                    <option value="day">{t('Day')}</option>
                    <option value="week">{t('Week')}</option>
                    <option value="month">{t('Month')}</option>
                  </Input>
                </Col>
                <Col md="4">
                  <Label for="returns-reason">{t('Reason')}</Label>
                  <Input id="returns-reason" name="reason" value={draftFilters.reason} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-category">{t('Reason category')}</Label>
                  <Input id="returns-category" type="select" name="reason_category" value={draftFilters.reason_category} onChange={updateDraft}>
                    <option value="">{t('All categories')}</option>
                    {Object.entries(REASON_CATEGORY_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{t(label)}</option>
                    ))}
                  </Input>
                </Col>
                <Col md="2">
                  <Label for="returns-listing">{t('Listing ID')}</Label>
                  <Input id="returns-listing" name="listing_id" value={draftFilters.listing_id} onChange={updateDraft} />
                </Col>
                <Col md="4">
                  <Label for="returns-customer">{t('Customer email')}</Label>
                  <Input id="returns-customer" type="email" name="customer_email" value={draftFilters.customer_email} onChange={updateDraft} />
                </Col>
                <Col md="2">
                  <Label for="returns-window">{t('Alert window (days)')}</Label>
                  <Input
                    id="returns-window"
                    type="number"
                    name="window_days"
                    min="1"
                    max="90"
                    value={draftFilters.window_days}
                    onChange={updateDraft}
                  />
                </Col>
                <Col md="2">
                  <Label for="returns-minimum">{t('Alert minimum quantity')}</Label>
                  <Input
                    id="returns-minimum"
                    type="number"
                    name="minimum_count"
                    min="1"
                    max="100"
                    value={draftFilters.minimum_count}
                    onChange={updateDraft}
                  />
                </Col>
                <Col md="8" className="d-flex gap-2 justify-content-md-end">
                  <Button type="button" color="light" onClick={resetFilters}>{t('Reset')}</Button>
                  <Button type="submit" color="primary">
                    <span className="bx bx-filter-alt" />
                    {' '}
                    {t('Apply filters')}
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>

        {error && <Alert color="danger">{error}</Alert>}

        <Row>
          <Col md="3">
            <Card>
              <CardBody>
                <p className="returns-trends-page__metric-label mb-2">{t('Returned quantity')}</p>
                <h3 className="returns-trends-page__metric-value mb-0">
                  {totals.quantity.toLocaleString()}
                </h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card>
              <CardBody>
                <p className="returns-trends-page__metric-label mb-2">{t('Return events')}</p>
                <h3 className="returns-trends-page__metric-value mb-0">
                  {totals.events.toLocaleString()}
                </h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card>
              <CardBody>
                <p className="returns-trends-page__metric-label mb-2">{t('Matching records')}</p>
                <h3 className="returns-trends-page__metric-value mb-0">
                  {Number(events.total || 0).toLocaleString()}
                </h3>
              </CardBody>
            </Card>
          </Col>
          <Col md="3">
            <Card>
              <CardBody>
                <p className="returns-trends-page__metric-label mb-2">{t('Active alerts')}</p>
                <h3 className="returns-trends-page__metric-value mb-0">
                  {alerts.length.toLocaleString()}
                </h3>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Card>
          <CardHeader className="d-flex flex-wrap justify-content-between align-items-center">
            <h5 className="mb-0">{t('Return alerts')}</h5>
            <span className="text-muted">
              {t('Comparing the latest {{days}} days with the previous period', {
                days: summary.alerts?.window_days || 14,
              })}
            </span>
          </CardHeader>
          <CardBody className="p-0">
            <div className="table-responsive">
              <Table className="table table-striped mb-0">
                <Thead>
                  <Tr>
                    <Th>{t('Alert')}</Th>
                    <Th>{t('Stock / listing')}</Th>
                    <Th>{t('Category')}</Th>
                    <Th>{t('Customer')}</Th>
                    <Th className="text-end">{t('Current')}</Th>
                    <Th className="text-end">{t('Previous')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {!summaryLoading && alerts.length === 0 && (
                    <Tr><Td colSpan="6" className="text-muted">{t('No active return alerts.')}</Td></Tr>
                  )}
                  {summaryLoading && (
                    <Tr><Td colSpan="6" className="text-center py-4"><Spinner size="sm" /></Td></Tr>
                  )}
                  {!summaryLoading && alerts.map((alert) => (
                    <Tr key={`${alert.type}-${alert.listing_id || alert.stock_id}-${alert.reason_category}-${alert.customer_email || alert.direction}`}>
                      <Td>
                        <Badge
                          color={alertColor(alert)}
                          className={`returns-trends-page__alert-badge returns-trends-page__alert-badge--${alertColor(alert)}`}
                        >
                          {alertLabel(alert)}
                        </Badge>
                      </Td>
                      <Td>
                        {renderAlertStockLinks(alert)}
                        {alert.listing_id && <small className="d-block text-muted">{`${t('Listing')} #${alert.listing_id}`}</small>}
                      </Td>
                      <Td>
                        {t(REASON_CATEGORY_LABELS[alert.reason_category] || 'Other')}
                      </Td>
                      <Td>{alert.customer_email || '—'}</Td>
                      <Td className="text-end">{Number(alert.current_qty || alert.returned_qty || 0).toLocaleString()}</Td>
                      <Td className="text-end">{alert.type === 'same_customer_cluster' ? '—' : Number(alert.previous_qty || 0).toLocaleString()}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </div>
          </CardBody>
        </Card>

        <Row>
          <Col xl="8">
            <Card>
              <CardHeader><h5 className="mb-0">{t('Return volume over time')}</h5></CardHeader>
              <CardBody>{summaryLoading ? <div className="text-center py-5"><Spinner /></div> : <Chart type="line" height={320} options={trendOptions} series={[{ name: t('Returned quantity'), data: summary.trend.map((row) => Number(row.returned_qty || 0)) }]} />}</CardBody>
            </Card>
          </Col>
          <Col xl="4">
            <Card>
              <CardHeader><h5 className="mb-0">{t('Return reasons')}</h5></CardHeader>
              <CardBody>{summaryLoading ? <div className="text-center py-5"><Spinner /></div> : <Chart type="donut" height={320} options={reasonOptions} series={summary.reasons.map((row) => Number(row.returned_qty || 0))} />}</CardBody>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xl="5">
            <Card>
              <CardHeader><h5 className="mb-0">{t('Top returned stock')}</h5></CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table className="table table-striped mb-0">
                    <Thead>
                      <Tr>
                        <Th>{t('Stock ID')}</Th>
                        <Th className="text-end">{t('Quantity')}</Th>
                        <Th className="text-end">{t('Events')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {summary.stock_items.length === 0 && <Tr><Td colSpan="3" className="text-muted">{t('No results.')}</Td></Tr>}
                      {summary.stock_items.map((row) => (
                        <Tr key={row.stock_id}>
                          <Td>{renderStockFilterLink(row.stock_id)}</Td>
                          <Td className="text-end">{Number(row.returned_qty || 0).toLocaleString()}</Td>
                          <Td className="text-end">{Number(row.return_count || 0).toLocaleString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col xl="7">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('Return events')}</h5>
                <span className="text-muted">{`${Number(events.total || 0).toLocaleString()} ${t('records')}`}</span>
              </CardHeader>
              <CardBody className="p-0">
                <div className="table-responsive">
                  <Table className="table table-striped mb-0">
                    <Thead>
                      <Tr>
                        <Th>{t('Returned')}</Th>
                        <Th>{t('Order')}</Th>
                        <Th>{t('Stock ID')}</Th>
                        <Th>{t('Reason')}</Th>
                        <Th>{t('Category')}</Th>
                        <Th>{t('Source')}</Th>
                        <Th className="text-end">{t('Qty')}</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {eventsLoading && <Tr><Td colSpan="7" className="text-center py-4"><Spinner size="sm" /></Td></Tr>}
                      {!eventsLoading && events.results.length === 0 && <Tr><Td colSpan="7" className="text-muted">{t('No results.')}</Td></Tr>}
                      {!eventsLoading && events.results.map((row) => (
                        <Tr key={row.id}>
                          <Td className="text-nowrap">{moment(row.returned_at).format('MMM D, YYYY')}</Td>
                          <Td>
                            {row.sale_id
                              ? (
                                <Link
                                  className="returns-trends-page__filter-link"
                                  to={`/sales/${row.sale_id}`}
                                >
                                  {row.order_number}
                                </Link>
                              )
                              : row.order_number}
                          </Td>
                          <Td>{renderStockFilterLink(row.stock_id)}</Td>
                          <Td>{renderReasonFilterLink(row.reason || t('Unknown'))}</Td>
                          <Td>
                            {t(REASON_CATEGORY_LABELS[row.reason_category] || 'Other')}
                          </Td>
                          <Td><Badge color="soft-info" className="text-dark">{t(SOURCE_LABELS[row.source] || row.source)}</Badge></Td>
                          <Td className="text-end">{Number(row.qty || 0).toLocaleString()}</Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </div>
              </CardBody>
              {lastPage > 1 && <div className="card-footer d-flex justify-content-end pb-0"><BasePagination currentPage={page} lastPage={lastPage} switchPage={setPage} /></div>}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

Returns.propTypes = { t: PropTypes.func.isRequired };

// Only Admin and Super Admin have access to the Returns Trends ability.
// Keep the data-fetching component unmounted for unauthorized direct visits.
export function ReturnsTrendsPage({ t }) {
  return (
    <>
      <Can i="see" this="Returns Trends"><Returns t={t} /></Can>
      <Can not i="see" this="Returns Trends">
        <div className="page-content">
          <div className="container-fluid"><Alert color="danger">{t('Unauthorized')}</Alert></div>
        </div>
      </Can>
    </>
  );
}

ReturnsTrendsPage.propTypes = { t: PropTypes.func.isRequired };

export default connect(() => ({}), {})(withTranslation()(ReturnsTrendsPage));
